/**
 * Panel mount utility.
 * Each tool module calls mountPanel() inside its init function to inject
 * its own HTML into the DOM instead of having it hardcoded in index.html.
 */

import { track } from './utils/analytics.js';
export function mountPanel(id, html) {
  const el = document.createElement('div');
  el.id = `panel-${id}`;
  el.className = 'tool-panel';
  el.dataset.toolPanel = id;
  el.style.display = 'none';
  el.innerHTML = html;
  document.getElementById('panels-container').appendChild(el);
  return el;
}

// ─── Tool lifecycle hooks ────────────────────────────────────────────────────
// Tools with timers/animations register callbacks here.
// main.js fires them on tool switch.

const lifecycles = new Map();

function ensureLC(toolId) {
  if (!lifecycles.has(toolId)) lifecycles.set(toolId, { activate: [], deactivate: [] });
  return lifecycles.get(toolId);
}

export function onActivate(toolId, fn) {
  ensureLC(toolId).activate.push(fn);
}

export function onDeactivate(toolId, fn) {
  ensureLC(toolId).deactivate.push(fn);
}

export function fireActivate(toolId) {
  lifecycles.get(toolId)?.activate.forEach((fn) => fn());
}

export function fireDeactivate(toolId) {
  lifecycles.get(toolId)?.deactivate.forEach((fn) => fn());
}

// ─── Cross-tool data passing ────────────────────────────────────────────────
// Allows any tool to send its output to another tool's input via "Open in..."

const prefillStore = new Map();
let _navigateFn = null;
const _openInTargets = [];
let _toolRelevance = {};

/** Register which tools accept text input (called by main.js at startup) */
export function registerOpenInTargets(targets, relevance) {
  _openInTargets.length = 0;
  _openInTargets.push(...targets);
  if (relevance) _toolRelevance = relevance;
}

/** Get all registered targets, optionally excluding the current tool */
export function getOpenInTargets(excludeId) {
  return _openInTargets.filter((t) => t.id !== excludeId);
}

/** Get relevance data for a source tool */
export function getToolRelevance(sourceId) {
  return _toolRelevance[sourceId] || null;
}

/** Register the navigation function (called by main.js at startup) */
export function setNavigator(fn) {
  _navigateFn = fn;
}

/** Consume and clear prefill data for a tool (returns null if none) */
export function consumePrefill(toolId) {
  const data = prefillStore.get(toolId);
  prefillStore.delete(toolId);
  return data || null;
}

/** Send data to another tool — stores prefill then navigates */
export function openInTool(toolId, data) {
  // A hand-over with nothing in it (no text, no date) is no prefill: the tool opens as it was
  // left, its saved session restored, instead of empty
  const given = data && Object.values(data).some((v) => v !== '' && v !== null && v !== undefined);
  if (given) prefillStore.set(toolId, data);
  if (_navigateFn) _navigateFn(toolId);
}

/**
 * Open a tool with its inputs and button modes set, as a share link does:
 * `state` is the { elementId: value } map that sessions capture (e.g. a key,
 * a mode and a ciphertext).
 */
export function openToolWithState(toolId, state) {
  if (_navigateFn) _navigateFn(toolId, { state });
}

// ─── Global copy-button delegation ──────────────────────────────────────────
// Any tool can add <button class="copy-btn" data-copy-target="element-id">Copy</button>
// and it works automatically — no per-tool handler needed.

function initCopyDelegation() {
  const container = document.getElementById('panels-container');
  if (!container) return;
  container.addEventListener('click', (e) => {
    const btn = e.target.closest('.copy-btn[data-copy-target]');
    if (!btn) return;
    const target = document.getElementById(btn.dataset.copyTarget);
    if (!target) return;
    const text = target.textContent;
    if (!text) return;
    track('copy', { tool: btn.closest('.tool-panel')?.dataset.toolPanel || '' });
    navigator.clipboard.writeText(text).then(() => {
      const orig = btn.textContent;
      btn.textContent = '\u2713';
      setTimeout(() => { btn.textContent = orig; }, 1500);
    }).catch(() => {});
  });
}

// Auto-init when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCopyDelegation);
} else {
  initCopyDelegation();
}
